import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpenses } from '../../contexts/ExpenseContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useCurrency } from '../../hooks/useCurrency';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCharacterState } from '../../contexts/CharacterContext';
import { formatDate, formatDateSlash, getCurrentMonthRange, toLocalISODate } from '../../utils/dateUtils';
import { validateExpenseForm } from '../../utils/validationUtils';
import { BarChart, Bar, Cell, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import RangeCalendar from './RangeCalendar';
import { summariseMoney } from '../../utils/moneySummary';
import { buildCategoryScale } from '../../utils/chartPalette';

const budgetTypes = ['Bill', 'Expense', 'Debt', 'Income', 'Saving'];
const budgetTabOrder = ['Income', 'Bill', 'Expense', 'Debt', 'Saving'];
const budgetTypeTint = { Income: 'tint-indigo', Bill: 'tint-orchid', Expense: 'tint-lavender', Debt: 'tint-rose', Saving: 'tint-mint' };
const transactionKinds = ['expense', 'income'];
const TRANSACTIONS_PAGE_SIZE = 10;
const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function formatAxisAmount(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(value);
}

function BudgetVsActualTooltip({ active, payload, label }) {
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('expenses');
  if (!active || !payload || !payload.length) return null;
  const budget = payload.find((entry) => entry.dataKey === 'Budget')?.value ?? 0;
  const actual = payload.find((entry) => entry.dataKey === 'Actual')?.value ?? 0;
  const left = budget - actual;
  return (
    <div className="chart-tooltip">
      <strong>{label}</strong>
      <span>{t('budgetVsActual.tooltipBudget', { value: formatCurrency(budget) })}</span>
      <span>{t('budgetVsActual.tooltipActual', { value: formatCurrency(actual) })}</span>
      <span className={left < 0 ? 'negative' : ''}>{left < 0 ? t('budgetVsActual.tooltipOverBy', { value: formatCurrency(Math.abs(left)) }) : t('budgetVsActual.tooltipLeft', { value: formatCurrency(left) })}</span>
    </div>
  );
}

function MonthlyTrendTooltip({ active, payload, label }) {
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation('expenses');
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <strong>{label}</strong>
      <span>{t('monthlyTrend.tooltipSpent', { value: formatCurrency(payload[0].value) })}</span>
    </div>
  );
}

function ExpenseDashboard() {
  const { t } = useTranslation('expenses');
  const { expenses, addExpense, updateExpense, deleteExpense, budgets, addBudget, updateBudget, deleteBudget, wallets } = useExpenses();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { formatCurrency, currency, toVnd, fromVnd } = useCurrency();
  const { isPro } = useAuth();
  const { limits, openUpgradeModal } = useSubscription();
  const { promptBigTransaction } = useCharacterState();
  const { t: tSub } = useTranslation('subscription');
  const budgetCountByType = useMemo(() => {
    const counts = {};
    budgets.forEach((budget) => {
      const type = budget.type || 'Expense';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [budgets]);
  const isBudgetTypeAtLimit = useCallback(
    (type) => !isPro && (budgetCountByType[type] || 0) >= limits.budgetsPerType,
    [isPro, budgetCountByType, limits.budgetsPerType],
  );
  const budgetTypeLabels = useMemo(() => ({
    Bill: t('budgetTypeLabels.Bill'),
    Expense: t('budgetTypeLabels.Expense'),
    Debt: t('budgetTypeLabels.Debt'),
    Income: t('budgetTypeLabels.Income'),
    Saving: t('budgetTypeLabels.Saving'),
  }), [t]);
  const otherCategoryLabel = t('form.otherCategory');
  // A wallet is the parent of every transaction, so new ones default straight to it
  // rather than starting unlinked.
  const defaultWalletId = wallets.find((wallet) => wallet.isDefault)?.id || wallets[0]?.id || '';
  const [periodRange, setPeriodRange] = useState(getCurrentMonthRange);
  const currentPeriodMonth = periodRange.start.slice(0, 7);
  const visibleBudgets = useMemo(
    () => budgets.filter((budget) => budget.recurring !== false || budget.month === currentPeriodMonth),
    [budgets, currentPeriodMonth],
  );
  const budgetCategories = useMemo(() => {
    const seen = new Map();
    visibleBudgets.forEach((budget) => {
      if (!seen.has(budget.category)) seen.set(budget.category, budget);
    });
    return Array.from(seen.values());
  }, [visibleBudgets]);
  const expenseCategoryOptions = useMemo(() => {
    const seen = new Map();
    visibleBudgets.filter((budget) => (budget.type || 'Expense') !== 'Income').forEach((budget) => {
      if (!seen.has(budget.category)) seen.set(budget.category, budget);
    });
    if (!seen.has(otherCategoryLabel)) seen.set(otherCategoryLabel, { category: otherCategoryLabel, type: 'Expense', isOther: true });
    return Array.from(seen.values());
  }, [visibleBudgets, otherCategoryLabel]);
  const incomeCategoryOptions = useMemo(() => {
    const seen = new Map();
    visibleBudgets.filter((budget) => (budget.type || 'Expense') === 'Income').forEach((budget) => {
      if (!seen.has(budget.category)) seen.set(budget.category, budget);
    });
    return Array.from(seen.values());
  }, [visibleBudgets]);
  const [form, setForm] = useState({ name: '', amount: '', category: expenseCategoryOptions[0]?.category || '', date: new Date().toISOString().slice(0, 10), notes: '', kind: 'expense', walletId: defaultWalletId });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ category: '', amount: '', type: 'Expense', recurring: true });
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetErrors, setBudgetErrors] = useState({});
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortKey, setSortKey] = useState('date');
  const [errors, setErrors] = useState({});
  const [periodDraft, setPeriodDraft] = useState(periodRange);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [periodError, setPeriodError] = useState('');
  const [transactionPage, setTransactionPage] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeBudgetType, setActiveBudgetType] = useState('Expense');

  const inPeriod = useCallback((item) => item.date >= periodRange.start && item.date <= periodRange.end, [periodRange]);

  const filteredExpenses = useMemo(() => {
    const next = expenses.filter((item) => {
      const matchesSearch = !search || item.name.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || item.category === categoryFilter;
      return matchesSearch && matchesCategory && inPeriod(item);
    });

    return next.sort((a, b) => {
      if (sortKey === 'amount') return b.amount - a.amount;
      return new Date(b.date) - new Date(a.date);
    });
  }, [expenses, search, categoryFilter, sortKey, inPeriod]);
  const totalTransactionPages = Math.max(1, Math.ceil(filteredExpenses.length / TRANSACTIONS_PAGE_SIZE));
  const currentTransactionPage = Math.min(transactionPage, totalTransactionPages);
  const paginatedExpenses = useMemo(
    () => filteredExpenses.slice((currentTransactionPage - 1) * TRANSACTIONS_PAGE_SIZE, currentTransactionPage * TRANSACTIONS_PAGE_SIZE),
    [filteredExpenses, currentTransactionPage],
  );
  const recentActivity = useMemo(
    () => expenses.filter(inPeriod).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5),
    [expenses, inPeriod],
  );
  const monthlyExpenses = useMemo(() => expenses.filter((item) => inPeriod(item) && (item.kind || 'expense') !== 'income'), [expenses, inPeriod]);
  const monthlyTotal = useMemo(() => monthlyExpenses.reduce((sum, item) => sum + item.amount, 0), [monthlyExpenses]);
  const biggestExpense = useMemo(() => (monthlyExpenses.length ? monthlyExpenses.reduce((max, item) => (item.amount > max.amount ? item : max), monthlyExpenses[0]) : null), [monthlyExpenses]);
  // Income / Spending / Saving for the selected period, from the shared helper so the
  // Dashboard's version of these three numbers can't drift from this one.
  const summary = useMemo(
    () => summariseMoney({ expenses, budgets: visibleBudgets, start: periodRange.start, end: periodRange.end }),
    [expenses, visibleBudgets, periodRange],
  );
  const actualFor = summary.actualFor;
  // Money in over the period. This counts every income transaction, not only those in a
  // category with an Income budget row - a one-off payment the user never budgeted for
  // is still money that arrived, and leaving it out made the figure quietly too low.
  const totalIncome = summary.income;
  const previousPeriodTotal = useMemo(() => {
    const start = new Date(periodRange.start);
    const end = new Date(periodRange.end);
    const daysInPeriod = Math.round((end - start) / 86400000) + 1;
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (daysInPeriod - 1));
    const prevStartIso = toLocalISODate(prevStart);
    const prevEndIso = toLocalISODate(prevEnd);
    return expenses
      .filter((item) => item.date >= prevStartIso && item.date <= prevEndIso && (item.kind || 'expense') !== 'income')
      .reduce((sum, item) => sum + item.amount, 0);
  }, [expenses, periodRange]);
  const spentDeltaPct = previousPeriodTotal > 0 ? ((monthlyTotal - previousPeriodTotal) / previousPeriodTotal) * 100 : null;
  // The old lookup indexed a cycling eight-colour array, so category nine silently
  // shared category one's colour, and the array itself failed colour-vision separation
  // (--indigo-color against --orchid-color is deltaE 0.9 under protanopia). Slots are now
  // assigned in fixed order from the validated --chart-* set, with a neutral remainder.
  const colorForCategory = useMemo(
    () => buildCategoryScale(budgetCategories.map((item) => item.category)),
    [budgetCategories],
  );
  const categoryColor = useCallback((category) => {
    if (category === otherCategoryLabel) return 'color-mix(in srgb, var(--text-color) 45%, transparent)';
    return colorForCategory(category);
  }, [colorForCategory, otherCategoryLabel]);
  const walletTagFor = useCallback((item) => {
    const name = wallets.find((wallet) => wallet.id === item.walletId)?.name;
    return name && name !== item.category ? ` · ${name}` : '';
  }, [wallets]);
  const [periodStartYear, periodStartMonth] = periodRange.start.split('-').map(Number);
  const barData = Array.from({ length: 6 }, (_, index) => {
    const month = new Date(periodStartYear, periodStartMonth - 1 - (5 - index), 1);
    const label = month.toLocaleString('en', { month: 'short' });
    return {
      name: label,
      total: expenses.filter((item) => item.date.startsWith(`${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`) && (item.kind || 'expense') !== 'income').reduce((sum, item) => sum + item.amount, 0),
    };
  });
  const trendAverage = barData.reduce((sum, entry) => sum + entry.total, 0) / barData.length;
  const hasTrendData = barData.some((entry) => entry.total > 0);

  // Still needed for the per-row budget-vs-actual chart and the "needs attention" list.
  const outflowBudgets = useMemo(() => visibleBudgets.filter((item) => !['Income', 'Saving'].includes(item.type || 'Expense')), [visibleBudgets]);
  const totalBudget = summary.budgeted;
  const totalActual = summary.spentAgainstBudgets;
  const totalLeft = summary.leftToSpend;
  // What the user is holding onto over this period: what they deliberately set aside,
  // plus whatever they budgeted to spend and didn't.
  const totalSaving = summary.saving;
  const spendPercent = totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0;
  const isOverBudget = totalActual > totalBudget;
  const isNearLimit = !isOverBudget && spendPercent >= 90;
  const budgetVsActualData = outflowBudgets.map((item) => ({ category: item.category, Budget: item.amount, Actual: actualFor(item) }));
  const billBudgets = useMemo(() => visibleBudgets.filter((item) => (item.type || 'Expense') === 'Bill'), [visibleBudgets]);
  const expenseBudgets = useMemo(() => visibleBudgets.filter((item) => (item.type || 'Expense') === 'Expense'), [visibleBudgets]);
  const debtBudgets = useMemo(() => visibleBudgets.filter((item) => (item.type || 'Expense') === 'Debt'), [visibleBudgets]);
  const incomeBudgets = useMemo(() => visibleBudgets.filter((item) => (item.type || 'Expense') === 'Income'), [visibleBudgets]);
  const savingBudgets = useMemo(() => visibleBudgets.filter((item) => (item.type || 'Expense') === 'Saving'), [visibleBudgets]);
  const budgetTypeItems = useMemo(() => ({
    Income: incomeBudgets, Bill: billBudgets, Expense: expenseBudgets, Debt: debtBudgets, Saving: savingBudgets,
  }), [incomeBudgets, billBudgets, expenseBudgets, debtBudgets, savingBudgets]);
  const budgetTypeTitle = useMemo(() => ({
    Income: t('budgetTables.income'),
    Bill: t('budgetTables.bills'),
    Expense: t('budgetTables.budget'),
    Debt: t('budgetTables.debt'),
    Saving: t('budgetTables.savings'),
  }), [t]);
  const periodStart = formatDateSlash(periodRange.start);
  const periodEnd = formatDateSlash(periodRange.end);

  const typeStatus = useCallback((type) => {
    if (type === 'Income' || type === 'Saving') return 'ok';
    const items = budgetTypeItems[type];
    if (items.some((item) => item.amount > 0 && actualFor(item) > item.amount)) return 'danger';
    if (items.some((item) => item.amount > 0 && actualFor(item) / item.amount >= 0.9)) return 'warn';
    return 'ok';
  }, [budgetTypeItems, actualFor]);

  const needsAttention = useMemo(() => {
    const issues = [];
    outflowBudgets.forEach((item) => {
      const actual = actualFor(item);
      if (item.amount <= 0) return;
      if (actual > item.amount) {
        issues.push({ key: `over-${item.id}`, level: 'danger', type: item.type || 'Expense', label: t('attention.categoryOver', { category: item.category, amount: formatCurrency(actual - item.amount) }) });
      } else if (actual / item.amount >= 0.9) {
        issues.push({ key: `near-${item.id}`, level: 'warn', type: item.type || 'Expense', label: t('attention.categoryNear', { category: item.category, percent: Math.round((actual / item.amount) * 100) }) });
      }
    });
    if (isOverBudget) {
      issues.unshift({ key: 'overall-over', level: 'danger', type: outflowBudgets[0]?.type || 'Expense', label: t('attention.overallOver', { amount: formatCurrency(Math.abs(totalLeft)) }) });
    } else if (isNearLimit) {
      issues.unshift({ key: 'overall-near', level: 'warn', type: outflowBudgets[0]?.type || 'Expense', label: t('attention.overallNear', { percent: spendPercent }) });
    }
    return issues;
  }, [outflowBudgets, actualFor, isOverBudget, isNearLimit, totalLeft, spendPercent, t, formatCurrency]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const validation = validateExpenseForm(form);
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    if (editingId) {
      updateExpense({ ...form, id: editingId, amount: toVnd(form.amount) });
      addToast(t('toast.transactionUpdated'), 'success');
      setEditingId(null);
    } else {
      const vndAmount = toVnd(form.amount);
      addExpense({ ...form, amount: vndAmount });
      addToast(t('toast.transactionAdded'), 'success');
      if (form.kind === 'expense' && vndAmount > 500000) {
        promptBigTransaction(form.name);
      }
    }

    setForm({ name: '', amount: '', category: expenseCategoryOptions[0]?.category || '', date: new Date().toISOString().slice(0, 10), notes: '', kind: 'expense', walletId: defaultWalletId });
    setErrors({});
    setShowForm(false);
  };

  const openAddTransaction = () => {
    setEditingId(null);
    setForm({ name: '', amount: '', category: expenseCategoryOptions[0]?.category || '', date: new Date().toISOString().slice(0, 10), notes: '', kind: 'expense', walletId: defaultWalletId });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setForm({ ...expense, amount: String(fromVnd(expense.amount)), walletId: expense.walletId || defaultWalletId });
    setShowForm(true);
  };

  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setForm({ name: '', amount: '', category: expenseCategoryOptions[0]?.category || '', date: new Date().toISOString().slice(0, 10), notes: '', kind: 'expense', walletId: defaultWalletId });
    setErrors({});
  }, [expenseCategoryOptions, defaultWalletId]);

  const openAddBudget = (type = 'Expense') => {
    if (isBudgetTypeAtLimit(type)) {
      openUpgradeModal('budget');
      return;
    }
    setEditingBudgetId(null);
    setBudgetForm({ category: '', amount: '', type, recurring: true });
    setBudgetErrors({});
    setShowBudgetForm(true);
    setActiveBudgetType(type);
  };

  const openEditBudget = (budget) => {
    setEditingBudgetId(budget.id);
    setBudgetForm({ category: budget.category, amount: String(fromVnd(budget.amount)), type: budget.type || 'Expense', recurring: budget.recurring !== false });
    setBudgetErrors({});
    setShowBudgetForm(true);
  };

  const closeBudgetForm = useCallback(() => {
    setShowBudgetForm(false);
    setEditingBudgetId(null);
    setBudgetForm({ category: '', amount: '', type: 'Expense', recurring: true });
    setBudgetErrors({});
  }, []);

  const handleBudgetSubmit = (event) => {
    event.preventDefault();
    const category = budgetForm.category.trim();
    const validation = {};
    if (!category) validation.category = t('budgetForm.categoryRequired');
    if (!budgetForm.amount || Number(budgetForm.amount) <= 0) validation.amount = t('budgetForm.amountPositive');
    if (visibleBudgets.some((item) => item.category === category && item.type === budgetForm.type && item.id !== editingBudgetId)) {
      validation.category = t('budgetForm.categoryExists');
    }
    if (Object.keys(validation).length) {
      setBudgetErrors(validation);
      return;
    }

    const payload = { category, amount: toVnd(budgetForm.amount), type: budgetForm.type, recurring: budgetForm.recurring };

    if (editingBudgetId) {
      updateBudget({ id: editingBudgetId, ...payload });
      addToast(t('toast.budgetUpdated'), 'success');
    } else {
      addBudget({ ...payload, month: currentPeriodMonth });
      addToast(t('toast.budgetAdded'), 'success');
    }

    closeBudgetForm();
  };

  const handleDeleteBudget = async (id) => {
    const budget = budgets.find((item) => item.id === id);
    const confirmed = await confirm(t('confirmDeleteBudget', { category: budget ? budget.category : '' }));
    if (!confirmed) return;
    deleteBudget(id);
    addToast(t('toast.budgetDeleted'), 'success');
  };

  const handleDelete = async (id) => {
    const expense = expenses.find((item) => item.id === id);
    const confirmed = await confirm(t('confirmDeleteTransaction', { name: expense ? expense.name : t('thisTransaction') }));
    if (!confirmed) return;
    deleteExpense(id);
    addToast(t('toast.transactionDeleted'), 'success');
  };

  const openPeriodForm = () => {
    setPeriodDraft(periodRange);
    setPeriodError('');
    setShowPeriodForm(true);
  };

  const closePeriodForm = useCallback(() => {
    setShowPeriodForm(false);
    setPeriodDraft(periodRange);
    setPeriodError('');
  }, [periodRange]);

  const handlePeriodSubmit = (event) => {
    event.preventDefault();
    if (!periodDraft.start || !periodDraft.end) {
      setPeriodError(t('periodForm.selectBothDates'));
      return;
    }
    setPeriodRange(periodDraft);
    setShowPeriodForm(false);
    setPeriodError('');
    addToast(t('toast.periodUpdated'), 'success');
  };

  const handleRangeSelect = (start, end) => {
    setPeriodDraft({ start, end });
    setPeriodError('');
  };

  const applyPeriodPreset = (preset) => {
    const now = new Date();
    let start;
    let end;
    if (preset === 'this-month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (preset === 'last-month') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (preset === 'last-3') {
      start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      start = new Date(now.getFullYear(), 0, 1);
      end = now;
    }
    setPeriodDraft({ start: toLocalISODate(start), end: toLocalISODate(end) });
    setPeriodError('');
  };

  const exportCsv = () => {
    const rows = [['Name', 'Amount', 'Type', 'Category', 'Date', 'Notes'], ...expenses.map((expense) => [expense.name, expense.amount, expense.kind === 'income' ? 'Income' : 'Expense', expense.category, expense.date, expense.notes])];
    const content = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'expenses.csv';
    link.click();
    URL.revokeObjectURL(url);
    addToast(t('toast.csvExported'), 'success');
  };

  const goToAttentionIssue = (issue) => {
    setActiveTab('budgets');
    setActiveBudgetType(issue.type);
  };

  const renderBudgetTypeTable = (type) => {
    const items = budgetTypeItems[type];
    const title = budgetTypeTitle[type];
    const tint = budgetTypeTint[type];
    const showLeft = type !== 'Income';
    const subtotalBudget = items.reduce((sum, item) => sum + item.amount, 0);
    const subtotalActual = items.reduce((sum, item) => sum + actualFor(item), 0);
    const subtotalLeft = subtotalBudget - subtotalActual;
    const columnCount = showLeft ? 6 : 5;
    return (
      <div className="sheet-table-card">
        <div className={`sheet-panel-header ${tint}`}>
          <span style={{ fontWeight: 600, fontSize: '1rem' }}>{title}</span>
          <span>
            {isBudgetTypeAtLimit(type) && (
              <span className="limit-hint at-cap">{tSub('limitHint', { used: budgetCountByType[type] || 0, limit: limits.budgetsPerType })}</span>
            )}
            <button onClick={() => openAddBudget(type)} className="text-btn add-btn">
              <span className="add-btn-icon" aria-hidden="true" />
              {t('budgetsTab.addCategory')}
            </button>
          </span>
        </div>
        <div className="sheet-table-scroll responsive-collapse">
          <table className={`sheet-table ${tint}`}>
            <thead>
              <tr>
                <th style={{ paddingLeft: '10px' }}>{t('budgetTables.category')}</th>
                <th className="numeric">{t('budgetTables.budgetCol')}</th>
                <th className="numeric">{t('budgetTables.actual')}</th>
                {showLeft && <th className="numeric">{t('budgetTables.left')}</th>}
                <th>{t('budgetTables.progress')}</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const spent = actualFor(item);
                const left = item.amount - spent;
                const pct = item.amount > 0 ? Math.min(100, Math.round((spent / item.amount) * 100)) : 0;
                const over = spent > item.amount;
                const near = !over && pct >= 90;
                return (
                  <tr key={item.id}>
                    <td>
                      <span className="category-dot" style={{ background: categoryColor(item.category) }} />
                      {item.category}
                      {item.recurring === false && <span className="one-time-badge" title={t('budgetTables.oneTimeTitle')}>{t('budgetTables.oneTime')}</span>}
                    </td>
                    <td className="numeric">{formatCurrency(item.amount)}</td>
                    <td className="numeric">{formatCurrency(spent)}</td>
                    {showLeft && <td className={`numeric${left < 0 ? ' negative' : ''}`}>{formatCurrency(left)}</td>}
                    <td>
                      <span className="mini-bar"><span className={over ? 'over' : near ? 'warn' : ''} style={{ width: `${pct}%` }} /></span>
                    </td>
                    <td>
                      <div className="goal-actions">
                        <button onClick={() => openEditBudget(item)} className="text-btn icon-btn" aria-label="Edit">✎</button>
                        <button onClick={() => handleDeleteBudget(item.id)} className="text-btn danger icon-btn" aria-label="Delete">✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr className="sheet-empty-row"><td colSpan={columnCount}>{t('budgetTables.empty', { title: title.toLowerCase() })}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <table className={`sheet-table sheet-table-footer ${tint} responsive-collapse`}>
          <tbody>
            <tr className="sheet-total-row">
              <td style={{ paddingLeft: '10px' }}>{t('budgetTables.total')}</td>
              <td className="numeric">{formatCurrency(subtotalBudget)}</td>
              <td className="numeric">{formatCurrency(subtotalActual)}</td>
              {showLeft && <td className={`numeric${subtotalLeft < 0 ? ' negative' : ''}`}>{formatCurrency(subtotalLeft)}</td>}
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>

        <div className="card-list responsive-list">
          {items.length === 0 && <div className="card-empty">{t('budgetTables.empty', { title: title.toLowerCase() })}</div>}
          {items.map((item) => {
            const spent = actualFor(item);
            const left = item.amount - spent;
            const pct = item.amount > 0 ? Math.min(100, Math.round((spent / item.amount) * 100)) : 0;
            const over = spent > item.amount;
            const near = !over && pct >= 90;
            return (
              <div className="card-row" key={item.id}>
                <div className="card-row-top">
                  <span className="name-cell">
                    <span className="category-dot" style={{ background: categoryColor(item.category) }} />
                    {item.category}
                    {item.recurring === false && <span className="one-time-badge" title={t('budgetTables.oneTimeTitle')}>{t('budgetTables.oneTime')}</span>}
                  </span>
                </div>
                <div className="card-row-meta">
                  <span>{t('budgetTables.budgetCol')} {formatCurrency(item.amount)}</span>
                  <span>{t('budgetTables.actual')} {formatCurrency(spent)}</span>
                </div>
                {showLeft && (
                  <div className="card-row-meta">
                    <span>{t('budgetTables.left')}</span>
                    <span className={left < 0 ? 'negative' : ''}>{formatCurrency(left)}</span>
                  </div>
                )}
                <span className="mini-bar"><span className={over ? 'over' : near ? 'warn' : ''} style={{ width: `${pct}%` }} /></span>
                <div className="goal-actions card-row-actions">
                  <button onClick={() => openEditBudget(item)} className="text-btn">{t('transactions.edit')}</button>
                  <button onClick={() => handleDeleteBudget(item.id)} className="text-btn danger">{t('transactions.delete')}</button>
                </div>
              </div>
            );
          })}
          {items.length > 0 && (
            <div className="card-row">
              <div className="card-row-top"><strong>{t('budgetTables.total')}</strong></div>
              <div className="card-row-meta">
                <span>{t('budgetTables.budgetCol')} {formatCurrency(subtotalBudget)}</span>
                <span>{t('budgetTables.actual')} {formatCurrency(subtotalActual)}</span>
              </div>
              {showLeft && (
                <div className="card-row-meta">
                  <span>{t('budgetTables.left')}</span>
                  <span className={subtotalLeft < 0 ? 'negative' : ''}>{formatCurrency(subtotalLeft)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    setTransactionPage(1);
  }, [search, categoryFilter, sortKey, periodRange]);

  useEffect(() => {
    if (!showForm && !showPeriodForm && !showBudgetForm) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (showForm) closeForm();
      else if (showPeriodForm) closePeriodForm();
      else if (showBudgetForm) closeBudgetForm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showForm, showPeriodForm, showBudgetForm, closeForm, closePeriodForm, closeBudgetForm]);

  return (
    <section className="page-card">
      <div className="page-header expense-header">
        <div>
          <h1 className="display-title">{t('title')}</h1>
        </div>
        <div className="page-actions">
          <button onClick={openAddTransaction} className="primary-btn">{t('addTransaction')}</button>
          <button onClick={exportCsv} className="primary-btn">{t('exportCsv')}</button>
        </div>
      </div>

      <div className="hero-card">
        <div className="hero-top">
          <button type="button" className="period-pill" onClick={openPeriodForm}>{periodStart} – {periodEnd}</button>
          <span className="hero-biggest">
            {biggestExpense
              ? t('hero.biggestSpendLine', { amount: formatCurrency(biggestExpense.amount), name: biggestExpense.name, date: formatDate(biggestExpense.date) })
              : t('hero.biggestSpendEmpty')}
          </span>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <label>{t('overview.totalIncome')}</label>
            <strong className="income-amount">{formatCurrency(totalIncome)}</strong>
          </div>
          <div className="hero-stat">
            <label>{t('overview.spending')}</label>
            <strong>{formatCurrency(monthlyTotal)}</strong>
            {spentDeltaPct !== null && (
              <span className={`hero-delta ${spentDeltaPct <= 0 ? 'good' : 'bad'}`}>
                {spentDeltaPct <= 0 ? '▼' : '▲'} {t('hero.vsLastPeriod', { percent: Math.abs(spentDeltaPct).toFixed(1) })}
              </span>
            )}
          </div>
          <div className="hero-stat">
            <label>{t('overview.saving')}</label>
            <strong className="income-amount">{formatCurrency(totalSaving)}</strong>
            {/* Saving is two things added together, which is not guessable from one
                number - spell out the split so it doesn't look wrong when it exceeds
                what the user has knowingly put aside. */}
            <span className="hero-substat">
              {t('hero.savingBreakdown', {
                setAside: formatCurrency(summary.setAside),
                unspent: formatCurrency(summary.unspent),
              })}
            </span>
          </div>
          <div className="hero-stat hero-stat-ring">
            <svg className="ring-svg" width="56" height="56" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r={RING_RADIUS} fill="none" stroke="var(--border-color)" strokeWidth="6" />
              <circle
                cx="30"
                cy="30"
                r={RING_RADIUS}
                fill="none"
                strokeWidth="6"
                strokeLinecap="round"
                stroke={isOverBudget ? 'var(--danger-color)' : isNearLimit ? 'var(--warning-color)' : 'var(--success-color)'}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - Math.min(100, Math.max(0, spendPercent)) / 100)}
                transform="rotate(-90 30 30)"
              />
            </svg>
            <div>
              <label>{t('leftToSpend.title')}</label>
              <strong className={isOverBudget ? 'negative' : isNearLimit ? 'warn' : ''}>{formatCurrency(totalLeft)}</strong>
              <div className={`status-pill${isOverBudget ? ' over-budget' : isNearLimit ? ' near-limit' : ''}`} style={{ marginTop: '0.25rem' }}>
                {isOverBudget ? t('leftToSpend.overBudget') : t('leftToSpend.percentUsed', { percent: spendPercent })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="attention-row">
        {needsAttention.length === 0 ? (
          <span className="attn-chip ok"><span className="dot" />{t('attention.allGood')}</span>
        ) : needsAttention.map((issue) => (
          <button key={issue.key} type="button" className={`attn-chip ${issue.level}`} onClick={() => goToAttentionIssue(issue)}>
            <span className="dot" />{issue.label}
          </button>
        ))}
      </div>

      <nav className="view-tabs">
        <button type="button" className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>{t('tabs.overview')}</button>
        <button type="button" className={activeTab === 'budgets' ? 'active' : ''} onClick={() => setActiveTab('budgets')}>{t('tabs.budgets')}</button>
        <button type="button" className={activeTab === 'transactions' ? 'active' : ''} onClick={() => setActiveTab('transactions')}>{t('tabs.transactions')}</button>
      </nav>

      {activeTab === 'overview' && (
        <div className="tab-panel">
          <div className="chart-row">
            <div className="sheet-panel">
              <div className="sheet-panel-header">{t('budgetVsActual.title')}</div>
              <div className="sheet-panel-body">
                <div className="chart-legend">
                  <span className="chart-legend-item"><i className="legend-dot legend-dot-budget" />{t('budgetVsActual.budget')}</span>
                  <span className="chart-legend-item"><i className="legend-dot legend-dot-under" />{t('budgetVsActual.actualUnder')}</span>
                  <span className="chart-legend-item"><i className="legend-dot legend-dot-over" />{t('budgetVsActual.actualOver')}</span>
                </div>
                {budgetVsActualData.length === 0 ? (
                  <p className="chart-empty">{t('budgetVsActual.empty')}</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={budgetVsActualData} layout="vertical" margin={{ left: 10 }} barGap={3} barCategoryGap="26%">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
                      <XAxis type="number" stroke="var(--text-color)" tickFormatter={formatAxisAmount} fontSize={11} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="category" stroke="var(--text-color)" width={95} fontSize={11} axisLine={false} tickLine={false} />
                      <Tooltip content={<BudgetVsActualTooltip />} cursor={{ fill: 'color-mix(in srgb, var(--primary-color) 8%, transparent)' }} />
                      <Bar dataKey="Budget" fill="color-mix(in srgb, var(--text-color) 14%, transparent)" radius={[6, 6, 6, 6]} barSize={12} />
                      <Bar dataKey="Actual" radius={[6, 6, 6, 6]} barSize={12}>
                        {budgetVsActualData.map((entry) => (
                          <Cell key={entry.category} fill={entry.Actual > entry.Budget ? 'var(--danger-color)' : 'var(--success-color)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="sheet-panel">
              <div className="sheet-panel-header">{t('monthlyTrend.title')}</div>
              <div className="sheet-panel-body">
                <div className="chart-legend">
                  <span className="chart-legend-item"><i className="legend-dot legend-dot-current" />{t('monthlyTrend.currentPeriod')}</span>
                  <span className="chart-legend-item"><i className="legend-dot legend-dot-past" />{t('monthlyTrend.pastMonths')}</span>
                </div>
                {!hasTrendData ? (
                  <p className="chart-empty">{t('monthlyTrend.empty')}</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={barData} barCategoryGap="32%">
                      <defs>
                        <linearGradient id="monthlyTrendGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--primary-color)" />
                          <stop offset="100%" stopColor="var(--secondary-color)" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                      <XAxis dataKey="name" stroke="var(--text-color)" fontSize={11} axisLine={false} tickLine={false} />
                      <YAxis stroke="var(--text-color)" width={50} tickFormatter={formatAxisAmount} fontSize={11} axisLine={false} tickLine={false} />
                      <Tooltip content={<MonthlyTrendTooltip />} cursor={{ fill: 'color-mix(in srgb, var(--primary-color) 8%, transparent)' }} />
                      <ReferenceLine y={trendAverage} stroke="color-mix(in srgb, var(--text-color) 35%, transparent)" strokeDasharray="4 4" />
                      <Bar dataKey="total" radius={[8, 8, 0, 0]} barSize={26}>
                        {barData.map((entry, index) => (
                          <Cell key={entry.name + index} fill={index === barData.length - 1 ? 'url(#monthlyTrendGradient)' : 'color-mix(in srgb, var(--primary-color) 22%, transparent)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="sheet-panel">
            <div className="sheet-panel-header">
              <span>{t('recent.title')}</span>
              <button type="button" className="text-btn" onClick={() => setActiveTab('transactions')}>{t('recent.viewAll')}</button>
            </div>
            <div className="sheet-panel-body recent-list">
              {recentActivity.length === 0 ? (
                <p className="chart-empty">{t('recent.empty')}</p>
              ) : recentActivity.map((item) => (
                <div className="recent-row" key={item.id}>
                  <span className="recent-left">
                    <span className="recent-name">{item.name}</span>
                    <span className="recent-meta"><span className="category-dot" style={{ background: categoryColor(item.category) }} />{item.category}{walletTagFor(item)} · {formatDate(item.date)}</span>
                  </span>
                  <span className={`recent-amt${item.kind === 'income' ? ' income-amount' : ''}`}>
                    {item.kind === 'income' ? '+' : '−'} {formatCurrency(item.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budgets' && (
        <div className="tab-panel">
          <div className="type-tabs">
            {budgetTabOrder.map((type) => {
              const items = budgetTypeItems[type];
              const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
              const status = typeStatus(type);
              return (
                <button
                  key={type}
                  type="button"
                  className={`type-tab ${budgetTypeTint[type]}${activeBudgetType === type ? ' active' : ''}`}
                  onClick={() => setActiveBudgetType(type)}
                >
                  <span className="type-tab-top">
                    <span className={`type-tab-dot ${status}`} />
                    {budgetTypeTitle[type]}
                  </span>
                  <span className="type-tab-sub">{items.length} {items.length === 1 ? t('budgetsTab.category') : t('budgetsTab.categories')} · {formatCurrency(subtotal)}</span>
                </button>
              );
            })}
          </div>
          {renderBudgetTypeTable(activeBudgetType)}
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="tab-panel">
          <div className="sheet-table-card">
            <div className="sheet-panel-header tint-pink">{t('transactions.title')}</div>
            <div className="sheet-table-toolbar filters">
              <div className="search-field">
                <input placeholder={t('transactions.searchPlaceholder')} aria-label="Search transactions by name" value={search} onChange={(event) => setSearch(event.target.value)} />
                {search && (
                  <button type="button" className="search-clear-btn" aria-label="Clear search" onClick={() => setSearch('')}>✕</button>
                )}
              </div>
              <select value={categoryFilter} aria-label="Filter by category" onChange={(event) => setCategoryFilter(event.target.value)}>
                <option value="All">{t('transactions.allCategories')}</option>
                {budgetCategories.map((item) => <option key={item.category} value={item.category}>{item.category}</option>)}
                {!budgetCategories.some((item) => item.category === otherCategoryLabel) && (
                  <option value={otherCategoryLabel}>{otherCategoryLabel}</option>
                )}
              </select>
              <select value={sortKey} aria-label="Sort transactions" onChange={(event) => setSortKey(event.target.value)}>
                <option value="date">{t('transactions.sortByDate')}</option>
                <option value="amount">{t('transactions.sortByAmount')}</option>
              </select>
            </div>

            <table className="sheet-table tint-pink transactions-table responsive-collapse">
              <colgroup>
                <col style={{ width: '13%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '40%' }} />
                <col style={{ width: '13%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('transactions.name')}</th>
                  <th className="numeric">{t('transactions.amount')}</th>
                  <th>{t('transactions.category')}</th>
                  <th>{t('transactions.date')}</th>
                  <th>{t('transactions.notes')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedExpenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>{expense.name}</td>
                    <td className={`numeric${expense.kind === 'income' ? ' income-amount' : ''}`}>{expense.kind === 'income' ? '+' : '−'} {formatCurrency(expense.amount)}</td>
                    <td><span className="category-dot" style={{ background: categoryColor(expense.category) }} />{expense.category}{walletTagFor(expense)}</td>
                    <td>{formatDate(expense.date)}</td>
                    <td className="notes-cell">{expense.notes}</td>
                    <td>
                      <div className="goal-actions">
                        <button onClick={() => handleEdit(expense)} className="text-btn">{t('transactions.edit')}</button>
                        <button onClick={() => handleDelete(expense.id)} className="text-btn danger">{t('transactions.delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredExpenses.length === 0 && (
                  <tr className="sheet-empty-row"><td colSpan={6}>{t('transactions.empty')}</td></tr>
                )}
              </tbody>
            </table>

            <div className="card-list responsive-list">
              {paginatedExpenses.map((expense) => (
                <div className="card-row" key={expense.id}>
                  <div className="card-row-top">
                    <span className="name-cell">{expense.name}</span>
                    <span className={`card-row-amt${expense.kind === 'income' ? ' income-amount' : ''}`}>{expense.kind === 'income' ? '+' : '−'} {formatCurrency(expense.amount)}</span>
                  </div>
                  <div className="card-row-meta">
                    <span><span className="category-dot" style={{ background: categoryColor(expense.category) }} />{expense.category}{walletTagFor(expense)}</span>
                    <span>{formatDate(expense.date)}</span>
                  </div>
                  {expense.notes && <div className="notes-cell">{expense.notes}</div>}
                  <div className="goal-actions card-row-actions">
                    <button onClick={() => handleEdit(expense)} className="text-btn">{t('transactions.edit')}</button>
                    <button onClick={() => handleDelete(expense.id)} className="text-btn danger">{t('transactions.delete')}</button>
                  </div>
                </div>
              ))}
              {filteredExpenses.length === 0 && <div className="card-empty">{t('transactions.empty')}</div>}
            </div>

            {totalTransactionPages > 1 && (
              <div className="pagination-controls">
                <button type="button" className="text-btn" disabled={currentTransactionPage === 1} onClick={() => setTransactionPage((page) => Math.max(1, page - 1))}>{t('transactions.prev')}</button>
                <span className="pagination-status">{t('transactions.page', { current: currentTransactionPage, total: totalTransactionPages })}</span>
                <button type="button" className="text-btn" disabled={currentTransactionPage === totalTransactionPages} onClick={() => setTransactionPage((page) => Math.min(totalTransactionPages, page + 1))}>{t('transactions.next')}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <form onSubmit={handleSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? t('form.editTitle') : t('form.addTitle')}</h3>
            <label>
              {t('form.type')}
              <select
                value={form.kind}
                onChange={(event) => {
                  const kind = event.target.value;
                  const nextOptions = kind === 'income' ? incomeCategoryOptions : expenseCategoryOptions;
                  setForm({ ...form, kind, category: nextOptions[0]?.category || '' });
                }}
              >
                {transactionKinds.map((kind) => <option key={kind} value={kind}>{kind === 'income' ? t('form.income') : t('form.expense')}</option>)}
              </select>
            </label>
            <label>
              {t('form.name')}
              <input autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              {errors.name && <span className="error-text">{t('validation.nameRequired')}</span>}
            </label>
            <label>
              {t('form.amount')}
              <div className="amount-input-group">
                <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
                <span className="amount-input-currency" title={currency.name}>{currency.symbol} {currency.code}</span>
              </div>
              {errors.amount && <span className="error-text">{t('validation.amountPositive')}</span>}
            </label>
            {form.kind === 'income' && incomeCategoryOptions.length === 0 ? (
              <div className="form-hint-block">
                <p className="form-hint">{t('form.addIncomeCategoryFirst')}</p>
                <button
                  type="button"
                  className="text-btn"
                  onClick={() => {
                    setShowForm(false);
                    openAddBudget('Income');
                  }}
                >
                  {t('form.addCategoryNow')}
                </button>
              </div>
            ) : (
              <label>
                {t('form.category')}
                <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  {(form.kind === 'income' ? incomeCategoryOptions : expenseCategoryOptions).map((item) => <option key={item.category} value={item.category}>{item.category}</option>)}
                </select>
                {errors.category && <span className="error-text">{t('validation.categoryRequired')}</span>}
              </label>
            )}
            <label>
              {t('form.date')}
              <input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
              {errors.date && <span className="error-text">{t('validation.dateRequired')}</span>}
            </label>
            {wallets.length > 0 && (
              <label>
                {t('form.wallet')}
                <select value={form.walletId || defaultWalletId} onChange={(event) => setForm({ ...form, walletId: event.target.value })}>
                  {wallets.map((wallet) => <option key={wallet.id} value={wallet.id}>{wallet.name}</option>)}
                </select>
                <span className="form-hint">{t('form.walletHint')}</span>
              </label>
            )}
            <label>
              {t('form.notes')}
              <textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            </label>
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeForm}>{t('form.cancel')}</button>
              <button className="primary-btn" type="submit">{editingId ? t('form.saveChanges') : t('form.addTransaction')}</button>
            </div>
          </form>
        </div>
      )}

      {showPeriodForm && (
        <div className="modal-overlay" onClick={closePeriodForm}>
          <form onSubmit={handlePeriodSubmit} className="card form-card modal-content period-modal" onClick={(event) => event.stopPropagation()}>
            <h3>{t('periodForm.title')}</h3>
            <div className="preset-row">
              <button type="button" className="preset-chip" onClick={() => applyPeriodPreset('this-month')}>{t('periodForm.presetThisMonth')}</button>
              <button type="button" className="preset-chip" onClick={() => applyPeriodPreset('last-month')}>{t('periodForm.presetLastMonth')}</button>
              <button type="button" className="preset-chip" onClick={() => applyPeriodPreset('last-3')}>{t('periodForm.presetLast3Months')}</button>
              <button type="button" className="preset-chip" onClick={() => applyPeriodPreset('ytd')}>{t('periodForm.presetThisYear')}</button>
            </div>
            <p className="form-hint" style={{ margin: 0 }}>{t('periodForm.customRange')}</p>
            <div className="range-cal-selection">
              {periodDraft.start ? formatDateSlash(periodDraft.start) : t('periodForm.startDate')}
              <span className="range-cal-arrow">→</span>
              {periodDraft.end ? formatDateSlash(periodDraft.end) : t('periodForm.endDate')}
            </div>
            <RangeCalendar start={periodDraft.start} end={periodDraft.end} onSelect={handleRangeSelect} />
            {periodError && <span className="error-text">{periodError}</span>}
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closePeriodForm}>{t('form.cancel')}</button>
              <button className="primary-btn" type="submit">{t('periodForm.apply')}</button>
            </div>
          </form>
        </div>
      )}

      {showBudgetForm && (
        <div className="modal-overlay" onClick={closeBudgetForm}>
          <form onSubmit={handleBudgetSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingBudgetId ? t('budgetForm.editTitle', { type: budgetTypeLabels[budgetForm.type] }) : t('budgetForm.addTitle', { type: budgetTypeLabels[budgetForm.type] })}</h3>
            <label>
              {t('budgetForm.category')}
              <input autoFocus placeholder={budgetForm.type === 'Income' ? t('budgetForm.categoryPlaceholderIncome') : t('budgetForm.categoryPlaceholderExpense')} value={budgetForm.category} onChange={(event) => setBudgetForm({ ...budgetForm, category: event.target.value })} />
              {budgetErrors.category && <span className="error-text">{budgetErrors.category}</span>}
            </label>
            <label>
              {t('budgetForm.amount', { code: currency.code })}
              <input type="number" min="0.01" step="0.01" value={budgetForm.amount} onChange={(event) => setBudgetForm({ ...budgetForm, amount: event.target.value })} />
              {budgetErrors.amount && <span className="error-text">{budgetErrors.amount}</span>}
            </label>
            {editingBudgetId && <p className="form-hint">{t('budgetForm.actualHint')}</p>}
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={budgetForm.recurring}
                onChange={(event) => setBudgetForm({ ...budgetForm, recurring: event.target.checked })}
              />
              {t('budgetForm.repeatMonthly')}
            </label>
            <p className="form-hint">
              {budgetForm.recurring ? t('budgetForm.recurringHint') : t('budgetForm.oneTimeHint')}
            </p>
            <label>
              {t('budgetForm.list')}
              <select value={budgetForm.type} onChange={(event) => setBudgetForm({ ...budgetForm, type: event.target.value })}>
                {budgetTypes.map((type) => <option key={type} value={type}>{budgetTypeLabels[type]}</option>)}
              </select>
            </label>
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeBudgetForm}>{t('budgetForm.cancel')}</button>
              <button className="primary-btn add-budget-btn" type="submit">{editingBudgetId ? t('budgetForm.saveChanges') : t('budgetForm.addBudget')}</button>
            </div>
          </form>
        </div>
      )}

    </section>
  );
}

export default ExpenseDashboard;
